import { ProductRecommendationOptions, UserSegmentTypes } from './use-user-segments';

/**
 * Minimal type alias for segment values — avoids circular dependency with personalization-controls.
 * The full PersonalizationSegmentValueMap in personalization-controls extends this shape.
 */
type PersonalizationSegmentValueMap = {
	[key in UserSegmentTypes]?: string | string[] | ProductRecommendationOptions;
} & {
	useOverrides?: boolean;
	enrollInExperiment?: boolean;
};

/**
 * The single query param used to pass personalization overrides via URL in non-prod.
 * Format: ?wac_persona=segment-type:value,another-type:val1|val2
 * Array values (e.g. owned-products, collection-options) are joined with "|".
 */
export const WAC_PERSONA_PARAM = 'wac_persona';

/** Segment types whose values are arrays. */
export const ARRAY_SEGMENT_TYPES = new Set<UserSegmentTypes>([
	UserSegmentTypes.OWNED_PRODUCTS,
	UserSegmentTypes.PRODUCT_USED_EVER,
	UserSegmentTypes.COLLECTION_OPTIONS,
]);

/**
 * Build the ?wac_persona= query string value from the current personalization
 * override values. Only segments with a non-empty value are included.
 * Array values are joined with "|".
 */
export const buildWacPersonaParam = (values: PersonalizationSegmentValueMap): string => {
	const parts: string[] = [];

	Object.values(UserSegmentTypes).forEach((segmentType) => {
		const raw = values[segmentType as keyof PersonalizationSegmentValueMap];
		if (raw === undefined || raw === null || raw === '') return;

		if (Array.isArray(raw)) {
			if (raw.length === 0) return;
			parts.push(`${segmentType}:${raw.join('|')}`);
		} else {
			parts.push(`${segmentType}:${raw}`);
		}
	});

	return parts.join(',');
};

/**
 * Parse a ?wac_persona= value back into a partial PersonalizationSegmentValueMap map.
 * Array segment types are split on "|".
 */
export const parseWacPersonaParam = (
	paramValue: string,
): Partial<PersonalizationSegmentValueMap> => {
	const result: Partial<PersonalizationSegmentValueMap> = {};
	if (!paramValue) return result;

	paramValue.split(',').forEach((part) => {
		const colonIdx = part.indexOf(':');
		if (colonIdx === -1) return;

		const segmentType = part.slice(0, colonIdx) as UserSegmentTypes;
		const value = part.slice(colonIdx + 1);

		if (!Object.values(UserSegmentTypes).includes(segmentType)) return;

		if (ARRAY_SEGMENT_TYPES.has(segmentType)) {
			(result as Record<string, unknown>)[segmentType] = value ? value.split('|') : [];
		} else {
			(result as Record<string, unknown>)[segmentType] = value;
		}
	});

	return result;
};

/**
 * Build the full shareable URL for the current personalization overrides.
 * In uniform-preview, reads the actual page path from the ?path= query param.
 * Falls back to compositionSlug, then window.location.pathname.
 */
export const getShareableUrl = (
	values: PersonalizationSegmentValueMap,
	compositionSlug?: string | null,
): string => {
	const personaValue = buildWacPersonaParam(values);
	if (!personaValue) return '';

	let pagePath: string;
	if (window.location.pathname.includes('/uniform-preview')) {
		const previewPath = new URLSearchParams(window.location.search).get('path');
		if (previewPath) {
			pagePath = previewPath;
		} else {
			const rawPath = compositionSlug ?? window.location.pathname;
			pagePath = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
		}
	} else {
		const rawPath = compositionSlug ?? window.location.pathname;
		pagePath = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
	}

	return `${window.location.origin}${pagePath}?${WAC_PERSONA_PARAM}=${encodeURIComponent(
		personaValue,
	)}`;
};
